
import React, { useEffect, useState, useRef } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Loader } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import './Contact.css';

const Contact = () => {
  const location = useLocation();
  const form = useRef();
  const [subject, setSubject] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inquiry = params.get('inquiry');
    if (inquiry) {
      setSubject(`Inquiry about: ${inquiry}`);
    }
  }, [location]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const sendEmail = (e) => {
    e.preventDefault();
    console.log('Form submission started');
    setIsSubmitting(true);
    setSubmitStatus(null);
    console.log('Sending email with params:', {
      service: 'service_lgri1rs',
      template: 'template_f05p3aa',
      key: 'FPO19oWZDVXA74M72'
    });

    emailjs
      .sendForm(
        'service_lgri1rs',
        'template_f05p3aa',
        form.current,
        {
          publicKey: 'FPO19oWZDVXA74M72',
        }
      )
      .then(
        () => {
          setSubmitStatus('success');
          setIsSubmitting(false);
          form.current.reset();
          setSubject('');
        },
        (error) => {
          console.error('FAILED...', error.text);
          setSubmitStatus('error');
          setIsSubmitting(false);
        }
      );
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us | Sotak Investment & Property" 
        description="Get in touch with Sotak for your real estate and construction needs. Call us or visit our office in Abuja."
      />
      <section className="page-header contact-header">
        <div className="container">
          <Reveal>
            <h1>Contact Us</h1>
            <p>We'd love to hear from you. Get in touch today.</p>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container contact-grid">
          {/* Contact Info */}
          <div className="contact-info">
            <Reveal>
              <h4 className="subtitle">Get In Touch</h4>
              <h2>Let's Start a Conversation</h2>
              <p>
                Whether you have a question about our services, projects, or investment opportunities, 
                our team is ready to assist you.
              </p>
            </Reveal>

            <Reveal delay={100} className="info-item">
              <div className="info-icon">
                <MapPin size={24} />
              </div>
              <div>
                <h5>Office Address</h5>
                <p>11 Seychelles street EFAB Metropolis Karsana Abuja</p>
              </div>
            </Reveal>

            <Reveal delay={200} className="info-item">
              <div className="info-icon">
                <Phone size={24} />
              </div>
              <div>
                <h5>Phone Number</h5>
                <p>08023046244<br />08070708844</p>
              </div>
            </Reveal>

            <Reveal delay={300} className="info-item">
              <div className="info-icon">
                <Mail size={24} />
              </div>
              <div>
                <h5>Email Address</h5>
                <p>sotakinvest@gmail.com</p>
              </div>
            </Reveal>

            <Reveal delay={400} className="info-item">
              <div className="info-icon">
                <Clock size={24} />
              </div>
              <div>
                <h5>Working Hours</h5>
                <p>Mon - Fri: 8:00 AM - 6:00 PM<br />Sat: 10:00 AM - 2:00 PM</p>
              </div>
            </Reveal>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="contact-form-wrapper">
            <Reveal direction="right">
              <form ref={form} onSubmit={sendEmail} className="contact-form">
                <h3>Send us a Message</h3>

                <div className="form-group">
                  <label>Full Name</label>
                  <input type="text" name="name" placeholder="Your Name" required />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" name="email" placeholder="Your Email" required />
                </div>
                <div className="form-group">
                  <label>Phone Number</label>
                  <input type="tel" name="phone" placeholder="Your Phone Number" />
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <select
                    name="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  >
                    <option value="">Select a Service</option>
                    <option value="Real Estate Development">Real Estate Development</option>
                    <option value="Property Management">Property Management</option>
                    <option value="Building & Construction">Building & Construction</option>
                    <option value="Investment Services">Investment Services</option>
                    <option value="General Contracts">General Contracts</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea name="message" rows="5" placeholder="How can we help you?" required></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div className="alert alert-success">
                    Message sent successfully! We'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="alert alert-error">
                    Failed to send message. Please try again or contact us directly.
                  </div>
                )}

                <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader className="spin" size={20} style={{marginRight: '8px', display: 'inline'}} />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
      
      {/* Map Placeholder */}
      <section className="map-section">
        <iframe 
          title="Google Map"
          src="https://maps.google.com/maps?q=11%20Seychelles%20street%20EFAB%20Metropolis%20Karsana%20Abuja&t=&z=15&ie=UTF8&iwloc=&output=embed" 
          width="100%" 
          height="450" 
          style={{border:0}} 
          allowFullScreen="" 
          loading="lazy">
        </iframe>
      </section>
    </div>
  );
};

export default Contact;
